import { Close } from "@mui/icons-material";
import { Button } from "@mui/material";
import classNames from "classnames";
import { MouseEventHandler, ReactNode } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import Avatar from "../../common/Avatar/Avatar";
import CustomLink from "../../ui/Link/Link";
import NavGroup from "../../ui/NavGroup/NavGroup";
import NavGroupItems from "../../ui/NavGroupItems/NavGroupItems";
import CustomNavbar from "../../ui/Navbar/Navbar";
import "./Navigation.scss";

export type platformUrl = { platform: { android: string; ios: string } };

export type NavItemProps = {
	label: string;
	img?: string;
	variant?: "primary" | "secondary";
	render?: boolean;
	requiredToRender?: string | boolean;
	additionalContentFrom?: ReactNode | string;
} & (
	| { url: string | platformUrl; external?: boolean; type?: never }
	| { type: "button"; url?: never; external?: never }
	| { type: "email"; url: string; external?: never }
	| { type: "avatar"; url: string; external?: never }
);

export const MobileNavigation = ({
	home,
	links,
	actions,
	functionsAvailableFromConfig,
	social,
	onClose,
}: {
	home: NavItemProps;
	links: NavItemProps[];
	actions: NavItemProps[];
	functionsAvailableFromConfig: {
		[key: string]: MouseEventHandler<HTMLButtonElement>;
	};
	social: NavItemProps[];
	onClose?: () => void;
}) => {
	return createPortal(
		<section className="site__navigation site__navigation--mobile">
			<header className="navigation__header">
				<Link to={home.url as string} onClick={onClose} className="home-link">
					<img
						src="/imagenes/FlyORKA-logo.svg"
						alt={home.label}
						className="logo"
					/>
				</Link>
				<Button
					className="mobile-nav__button mobile-nav__button--close"
					onClick={onClose}
					aria-label="Close Mobile Menu">
					<Close sx={{ fontSize: "3.5rem", color: "var(--brand-primary)" }} />
				</Button>
			</header>
			<CustomNavbar>
				<NavGroup className="nav-links">
					<NavGroupItems items={links} />
				</NavGroup>
				<NavGroup className="nav-actions">
					<NavGroupItems
						items={actions}
						functionsAvailableFromConfig={functionsAvailableFromConfig}
					/>
				</NavGroup>
			</CustomNavbar>
			<footer className="navigation__footer text-light">
				<CustomNavbar>
					<NavGroup className="nav-social">
						<NavGroupItems items={social} isIconGroup />
					</NavGroup>
				</CustomNavbar>
				<a href="mailto:hello@flyorka.com" className="link--email">
					hello@flyorka.com
				</a>
			</footer>
		</section>,
		document.body,
	);
};

const ConditionalWrapper = ({
	children,
	validAuth,
}: {
	children: ReactNode;
	validAuth: boolean;
}) => (validAuth ? <div role="navigation">{children}</div> : <>{children}</>);

export const HeaderNavigation = ({
	links,
	actions,
	className,
	functionsAvailableFromConfig,
	validAuth,
}: {
	links: NavItemProps[];
	actions: NavItemProps[];
	className?: string;
	functionsAvailableFromConfig: {
		[key: string]: MouseEventHandler<HTMLButtonElement>;
	};
	validAuth?: boolean;
}) => {
	const avatarLink = links.find((link) => link.type === "avatar");
	const filteredLinks = links.filter((link) => link.type !== "avatar");

	return (
		<ConditionalWrapper validAuth={validAuth || false}>
			<CustomNavbar
				className={classNames(
					"site__navigation site__navigation--desktop",
					{ "site__navigation--signed-in": validAuth },
					className,
				)}>
				<NavGroup className="nav-links">
					<NavGroupItems items={filteredLinks} />
				</NavGroup>
				<NavGroup className="nav-actions">
					<NavGroupItems
						items={actions}
						functionsAvailableFromConfig={functionsAvailableFromConfig}
					/>
				</NavGroup>
			</CustomNavbar>
			{validAuth && avatarLink && (
				<CustomLink link={avatarLink} className="link--avatar">
					<div className="avatar-container">
						<Avatar />
					</div>
				</CustomLink>
			)}
		</ConditionalWrapper>
	);
};
